import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  changeLegalPolicyStatus,
  createLegalPolicy,
  getLegalPolicies,
  getLegalPolicyHistory,
  updateLegalPolicy,
} from '@/lib/api';
import {
  EVENT_LOCALES,
  POLICY_TYPES,
  displayValue,
  formatDate,
  formatDateTime,
  isPolicyEditable,
  localeLabel,
  policyStatusMeta,
  policyTypeLabel,
} from '@/lib/eventStudio';
import { approvedV2Payload } from '@/lib/eventPolicyV2';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StatusPill, adminInputClass } from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';
import { Lock, Plus, Save, Scale, ShieldCheck } from 'lucide-react';

export default function AdminLegalPolicies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [locale, setLocale] = useState('en');
  const [approvalNote, setApprovalNote] = useState('');
  const [preparingV2, setPreparingV2] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getLegalPolicies();
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems(list);
      setApprovalNote(data?.requires_legal_approval_note || '');
      setSelectedId((current) => current || list[0]?.id || '');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId],
  );

  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    setDraft({
      title: selected.title || '',
      summary: selected.summary || '',
      content_html: selected.content_html || '',
      effective_date: selected.effective_date || '',
      pdf_url: selected.pdf_url || '',
    });
    getLegalPolicyHistory(selected.id)
      .then(({ data }) => setHistory(data))
      .catch(console.error);
  }, [selected]);

  const grouped = useMemo(() => POLICY_TYPES.map((type) => ({
    ...type,
    versions: items
      .filter((item) => item.type === type.value && item.locale === locale)
      .sort((a, b) => (b.version || 0) - (a.version || 0)),
  })), [items, locale]);

  const startVersion = async (type) => {
    try {
      // Carry the current text forward so a new version is an edit of what is
      // live, not a blank page someone has to paste back into.
      const current = items.find(
        (item) => item.type === type && item.locale === locale && item.status === 'published',
      );
      const { data } = await createLegalPolicy({
        type,
        locale,
        title: current?.title || policyTypeLabel(type),
        summary: current?.summary || '',
        content_html: current?.content_html || '',
        effective_date: current?.effective_date || '',
        pdf_url: current?.pdf_url || '',
      });
      toast.success(`${policyTypeLabel(type)} v${data.version} drafted`);
      setSelectedId(data.id);
      load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not start a new version');
    }
  };

  const prepareApprovedV2Drafts = async () => {
    if (preparingV2) return;
    try {
      setPreparingV2(true);
      let created = 0;
      for (const { value: type } of POLICY_TYPES) {
        const versions = items.filter((item) => item.type === type && item.locale === locale);
        if (versions.some((item) => Number(item.version) >= 2)) continue;
        const payload = approvedV2Payload(type, locale);
        if (!payload) continue;
        await createLegalPolicy(payload);
        created += 1;
      }
      toast.success(created
        ? `${created} approved v2 policy draft${created === 1 ? '' : 's'} prepared`
        : 'Version 2 drafts already exist; nothing was changed');
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not prepare the approved v2 drafts');
    } finally {
      setPreparingV2(false);
      await load();
    }
  };

  const save = async () => {
    if (!selected || !draft || saving) return;
    try {
      setSaving(true);
      await updateLegalPolicy(selected.id, draft);
      toast.success('Policy saved');
      load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not save this version');
    } finally {
      setSaving(false);
    }
  };

  const moveStatus = async (status) => {
    if (!selected) return;
    try {
      await changeLegalPolicyStatus(selected.id, { status });
      toast.success(`Policy ${status}`);
      load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not change the status');
    }
  };

  const editable = isPolicyEditable(selected);

  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-semibold text-[#062B1A]"
            style={{ fontFamily: 'var(--font-display)' }}>
            <Scale className="h-6 w-6 text-[#D4AF37]" />
            Legal & Policies
          </h2>
          <p className="text-sm text-[#062B1A]/60">
            The documents every registration is evidence against. A published version can never be
            edited — changing the wording creates a new version and retires the old one.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {locale === 'en' && (
            <Button type="button" variant="outline" onClick={prepareApprovedV2Drafts}
              disabled={preparingV2 || loading}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
              <Plus className="mr-2 size-4" />
              {preparingV2 ? 'Preparing v2 drafts…' : 'Prepare approved v2 drafts'}
            </Button>
          )}
          <Select value={locale} onValueChange={setLocale}>
            <SelectTrigger aria-label="Policy locale" className={cn('h-9 w-[9rem]', adminInputClass)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EVENT_LOCALES.map((item) => (
                <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {approvalNote && (
        <div data-testid="legal-approval-note"
          className="flex gap-2 rounded-lg border border-[rgba(212,175,55,0.45)] bg-[rgba(212,175,55,0.08)] p-3">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-[#8A6508]" />
          <p className="text-xs text-[#062B1A]/75">{approvalNote}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="space-y-4 p-3">
            {loading && <p className="px-2 py-3 text-sm text-[#062B1A]/50">Loading…</p>}
            {grouped.map((type) => (
              <div key={type.value} className="space-y-1">
                <div className="flex items-center justify-between gap-2 px-1">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                    {type.label}
                  </p>
                  <Button type="button" size="icon" variant="ghost"
                    aria-label={`New ${type.label} version`}
                    onClick={() => startVersion(type.value)}
                    className="h-6 w-6 text-[#8A6508]">
                    <Plus className="size-3.5" />
                  </Button>
                </div>
                {type.versions.length === 0 && (
                  <p className="px-2 py-1 text-xs text-[#062B1A]/40">No version yet.</p>
                )}
                {type.versions.map((policy) => (
                  <button
                    key={policy.id}
                    type="button"
                    data-testid="policy-version"
                    onClick={() => setSelectedId(policy.id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm',
                      policy.id === selectedId
                        ? 'bg-[#062B1A] text-white'
                        : 'text-[#062B1A] hover:bg-[#F1F3F5]',
                    )}
                  >
                    <span className="truncate">v{policy.version}</span>
                    <StatusPill tone={policyStatusMeta(policy.status).pillTone} className="shrink-0">
                      {policyStatusMeta(policy.status).label}
                    </StatusPill>
                  </button>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="space-y-4 p-5">
            {!selected || !draft ? (
              <p className="py-10 text-center text-sm text-[#062B1A]/45">
                Pick a version, or start a new one for a policy type.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-base font-semibold text-[#062B1A]"
                      style={{ fontFamily: 'var(--font-display)' }}>
                      {policyTypeLabel(selected.type)} — v{selected.version}
                    </p>
                    <p className="text-xs text-[#062B1A]/55">
                      {selected.policy_id} · {localeLabel(selected.locale)} ·{' '}
                      {selected.published_at ? `published ${formatDate(selected.published_at)}` : 'not published'}
                    </p>
                  </div>
                  <StatusPill tone={policyStatusMeta(selected.status).pillTone}>
                    {policyStatusMeta(selected.status).label}
                  </StatusPill>
                </div>

                {!editable && (
                  <div data-testid="policy-locked"
                    className="flex items-center gap-2 rounded-lg border border-[rgba(6,43,26,0.10)] bg-[#F7F8FA] p-3">
                    <Lock className="size-4 shrink-0 text-[#062B1A]/50" />
                    <p className="text-xs text-[#062B1A]/70">
                      This version is locked. People have agreed to exactly this text, so it cannot
                      change — use “New version” to draft an update.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="policy_title">Title</Label>
                    <Input id="policy_title" value={draft.title} disabled={!editable}
                      onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      className={adminInputClass} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="policy_effective">Effective date</Label>
                    <Input id="policy_effective" type="date" value={draft.effective_date} disabled={!editable}
                      onChange={(e) => setDraft({ ...draft, effective_date: e.target.value })}
                      className={adminInputClass} />
                  </div>
                  <div className="lg:col-span-2 space-y-1.5">
                    <Label htmlFor="policy_summary">Summary</Label>
                    <Input id="policy_summary" value={draft.summary} disabled={!editable}
                      onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                      className={adminInputClass} />
                  </div>
                  <div className="lg:col-span-2 space-y-1.5">
                    <Label htmlFor="policy_content">Content</Label>
                    <Textarea id="policy_content" rows={14} value={draft.content_html} disabled={!editable}
                      onChange={(e) => setDraft({ ...draft, content_html: e.target.value })}
                      className={cn('font-mono text-xs', adminInputClass)} />
                  </div>
                  <div className="lg:col-span-2 space-y-1.5">
                    <Label htmlFor="policy_pdf">PDF attachment URL (optional)</Label>
                    <Input id="policy_pdf" value={draft.pdf_url} disabled={!editable}
                      onChange={(e) => setDraft({ ...draft, pdf_url: e.target.value })}
                      className={adminInputClass} />
                    {draft.pdf_url && (
                      <a href={draft.pdf_url} target="_blank" rel="noreferrer"
                        className="inline-flex text-xs font-medium text-[#062B1A] underline underline-offset-2">
                        Preview official policy PDF
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {editable && (
                    <Button onClick={save} disabled={saving}
                      className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                      <Save className="mr-2 size-4" /> {saving ? 'Saving…' : 'Save draft'}
                    </Button>
                  )}
                  {selected.status === 'draft' && (
                    <Button variant="outline" onClick={() => moveStatus('review')}
                      className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                      Send for review
                    </Button>
                  )}
                  {['draft', 'review'].includes(selected.status) && (
                    <Button variant="outline" onClick={() => moveStatus('published')}
                      className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                      Publish this version
                    </Button>
                  )}
                  {selected.status === 'published' && (
                    <Button variant="outline" onClick={() => moveStatus('retired')}
                      className="border-[rgba(153,27,27,0.3)] text-[rgb(153,27,27)]">
                      Retire
                    </Button>
                  )}
                </div>

                {history?.versions?.length > 0 && (
                  <div>
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45">
                      Version history
                    </p>
                    <div className="space-y-1">
                      {history.versions.map((version) => (
                        <div key={version.id} data-testid="policy-history-row"
                          className="flex flex-wrap items-baseline gap-2 text-xs text-[#062B1A]/70">
                          <span className="font-mono">v{version.version}</span>
                          <StatusPill tone={policyStatusMeta(version.status).pillTone}>
                            {policyStatusMeta(version.status).label}
                          </StatusPill>
                          <span>{displayValue(version.effective_date)}</span>
                          <span className="text-[#062B1A]/45">
                            {version.published_at ? formatDateTime(version.published_at) : 'never published'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
